import { makeAuthenticatedRequest } from './authService';

/**
 * Memory Technique Service
 * Handles AI-powered story generation for memory techniques and database persistence
 */

export interface StoryGenerationRequest {
  pairs: number[];
  pictures: string[];
  originalNumber: string;
}

export interface MemoryTechniqueEntry {
  id?: number;
  user_id: number;
  number: string;
  story: string;
  pairs: number[];
  picture_associations: Array<{ number: number; picture: string }>;
  image_urls?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface CreateEntryData {
  user_id: number;
  number: string;
  story: string;
  pairs: number[];
  picture_associations: Array<{ number: number; picture: string }>;
  image_urls?: string[];
}

export interface UpdateEntryData {
  number?: string;
  story?: string;
  pairs?: number[];
  picture_associations?: Array<{ number: number; picture: string }>;
  image_urls?: string[];
}

/**
 * Generate a story using AI to connect picture associations
 */
export async function generateStory(request: StoryGenerationRequest): Promise<string> {
  try {
    const { pairs, pictures, originalNumber } = request;
    
    // Build system prompt for memory technique story generation
    const systemPrompt = `You are a memory technique expert specializing in creating memorable stories using the Major System mnemonic technique.

Your task is to create a vivid, memorable story that connects a series of pictures/words in sequence. The story should:
- Connect each picture naturally and sequentially
- Use action verbs and vivid imagery
- Create a narrative flow that makes the sequence easy to remember
- Be concise but engaging (2-4 sentences total)
- Make each picture/word clearly identifiable in the story

The pictures represent numbers using the Major System, and the story helps memorize the original number: ${originalNumber}`;

    // Build user message with picture associations
    const pictureList = pictures.map((pic, index) => `${index + 1}. ${pic} (represents ${pairs[index]})`).join('\n');
    
    const userMessage = `Create a memorable story connecting these pictures in order:

${pictureList}

Write a short, vivid story that connects all these pictures sequentially. Make it memorable and easy to visualize.`;

    // Call AI API
    const response = await makeAuthenticatedRequest('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({
        provider: 'claude',
        model: 'claude-haiku-4-5',
        systemPrompt: systemPrompt,
        messages: [
          {
            role: 'user',
            content: userMessage
          }
        ],
        maxTokens: 500,
        temperature: 0.8
      })
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to generate story');
    }

    // Extract the story text from the response
    const story = response.data?.response || response.data?.content?.find((c: any) => c.type === 'text')?.text || '';
    
    if (!story) {
      throw new Error('No story generated');
    }

    return story.trim();
  } catch (error) {
    console.error('Error generating story:', error);
    
    // Return a fallback story if AI fails
    const fallbackStory = request.pictures.join(' → ');
    return `A story connecting: ${fallbackStory}. Visualize each picture in sequence to remember the number ${request.originalNumber}.`;
  }
}

/**
 * Generate an image based on the story using AI
 */
export async function generateStoryImage(story: string, originalNumber: string): Promise<Array<{data: string, mimeType: string, alt?: string}>> {
  try {
    // Build system prompt for image generation
    const systemPrompt = `You are a visual memory technique assistant. Generate a vivid, memorable image that visualizes the entire memory story. The image should help the user remember the number ${originalNumber} by depicting the story in a clear, memorable way.`;

    const userMessage = `Generate an image that visualizes this memory story:

${story}

Create a single, vivid image that captures the entire story and helps memorize the number ${originalNumber}.`;

    // Call AI API with image generation enabled
    const response = await makeAuthenticatedRequest('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({
        provider: 'gemini',
        model: 'gemini-2.5-flash-image',
        systemPrompt: systemPrompt,
        messages: [
          {
            role: 'user',
            content: userMessage
          }
        ],
        maxTokens: 500,
        temperature: 0.8,
        responseModalities: ['TEXT', 'IMAGE'] // Enable image generation
      })
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to generate image');
    }

    // Extract images from the response
    const contentImages = response.data?.content?.filter((c: any) => c.type === 'image') || 
                         response.content?.filter((c: any) => c.type === 'image') || [];
    
    const processedImages = contentImages
      .filter((img: any): img is {type: string, data: string, mimeType: string} => 
        Boolean(img.data && typeof img.data === 'string')
      )
      .map((img: any) => ({
        data: img.data,
        mimeType: img.mimeType || 'image/png',
        alt: `Memory technique visualization for number ${originalNumber}`
      }));

    return processedImages;
  } catch (error) {
    console.error('Error generating image:', error);
    throw error;
  }
}

/**
 * Upload images to Firebase Storage
 */
export async function uploadImages(
  images: Array<{data: string, mimeType: string, alt?: string}>,
  entryId: number | string
): Promise<string[]> {
  if (!images || images.length === 0) {
    return [];
  }

  try {
    const { storage } = await import('../firebase/config');
    const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');

    const uploadPromises = images.map(async (img) => {
      // Convert base64 to blob
      const base64Response = await fetch(`data:${img.mimeType};base64,${img.data}`);
      const blob = await base64Response.blob();

      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(7);
      const extension = img.mimeType.split('/')[1] || 'png';
      const fileName = `${timestamp}_${randomId}.${extension}`;
      const storagePath = `memory-technique/${entryId}/images/${fileName}`;

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, blob, { contentType: img.mimeType });

      // Get public URL
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    });

    const urls = await Promise.all(uploadPromises);
    return urls;
  } catch (error) {
    console.error('Error uploading images:', error);
    throw error;
  }
}

/**
 * Create a new memory technique entry
 */
export async function createEntry(userId: number, entryData: CreateEntryData): Promise<{ success: boolean; id?: number; message?: string }> {
  try {
    const response = await makeAuthenticatedRequest('/memory-technique/entries', {
      method: 'POST',
      body: JSON.stringify(entryData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create entry');
    }

    return {
      success: true,
      id: response.id
    };
  } catch (error) {
    console.error('Error creating memory technique entry:', error);
    throw error;
  }
}

/**
 * Get all entries for a user
 */
export async function getUserEntries(userId: number): Promise<{ success: boolean; entries?: MemoryTechniqueEntry[]; count?: number }> {
  try {
    const response = await makeAuthenticatedRequest(`/memory-technique/entries/user/${userId}`, {
      method: 'GET'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch entries');
    }

    return {
      success: true,
      entries: response.entries || [],
      count: response.count || 0
    };
  } catch (error) {
    console.error('Error fetching memory technique entries:', error);
    throw error;
  }
}

/**
 * Get entry by ID
 */
export async function getEntryById(entryId: number): Promise<{ success: boolean; entry?: MemoryTechniqueEntry }> {
  try {
    const response = await makeAuthenticatedRequest(`/memory-technique/entries/${entryId}`, {
      method: 'GET'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch entry');
    }

    return {
      success: true,
      entry: response.entry
    };
  } catch (error) {
    console.error('Error fetching memory technique entry:', error);
    throw error;
  }
}

/**
 * Update an existing entry
 */
export async function updateEntry(entryId: number, entryData: UpdateEntryData): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await makeAuthenticatedRequest(`/memory-technique/entries/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(entryData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update entry');
    }

    return {
      success: true,
      message: response.message
    };
  } catch (error) {
    console.error('Error updating memory technique entry:', error);
    throw error;
  }
}

/**
 * Delete an entry
 */
export async function deleteEntry(entryId: number): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await makeAuthenticatedRequest(`/memory-technique/entries/${entryId}`, {
      method: 'DELETE'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete entry');
    }

    return {
      success: true,
      message: response.message
    };
  } catch (error) {
    console.error('Error deleting memory technique entry:', error);
    throw error;
  }
}

